import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { Plan, Pricing } from './types/pricing.types';
import { environment } from 'src/environments/environment';
import
{
  extractAddonName,
  extractPlanName,
  extractPlanInterval,
  isAddon,
  isPlan,
  extractAddonInterval,
} from './utils/extract.stripe';
import { VerificationData, VerificationResponse } from './types/sign-up.types';

@Injectable()
export class SignUpService
{
  private googleMapsApiKey = environment.googleMapsApiKey;
  private scriptLoaded = false;

  private _pricingInfo = new BehaviorSubject<Pricing>(null);
  public pricingInfo$ = (this._pricingInfo as Subject<Pricing>)
    .asObservable()
    .pipe(filter((pricing) => !!pricing));
  constructor(private _http: HttpClient) { }

  getPricingPlans()
  {
    const separator = '_';
    return this._http.get<Plan[]>(`${environment.API_URL}/StripeProductos/SignUp`).pipe(
      map((products) =>
      {
        const addons = products
          .filter((addon) =>
          {
            if (isAddon(addon.Name))
            {
              return addon;
            }
          })
          .map((addon) => ({
            ...addon,
            Interval: extractAddonInterval(addon.Name),
            Name: extractAddonName(addon.Name),
          }));
        const plans = products
          .filter((plan) =>
          {
            if (isPlan(plan.Name))
            {
              return plan;
            }
          })
          .sort(
            (a, b) => +a.Name.split(separator)[0] - +b.Name.split(separator)[0]
          )
          .map((plan) => ({
            ...plan,
            Interval: extractPlanInterval(plan.Name),
            Name: extractPlanName(plan.Name).split('-')[1].trim(),
          }));
        return { addons, plans } as Pricing;
      }),
      tap((pricing) => this._pricingInfo.next(pricing))
    );
  }

  comprobarCorreo(email: string)
  {
    return this._http.get<number>(
      `${environment.API_URL}/ValidacionRegistroCorreo?Correo=` + email
    );
  }

  verificarRegistro(payload: VerificationData)
  {
    return this._http.post<number>(`${environment.API_URL}/CrearVerificacionSMS`, payload);
  }

  getJsonCountries()
  {
    return this._http.get<any>('/assets/countriesJSON.json');
  }

  verificarCuenta(PIN: number, Codigo: number)
  {
    return this._http.get<VerificationResponse>(
      `${environment.API_URL}/VerificarCodigoSMS`,
      {
        params: { PIN, Codigo },
      }
    );
  }

  loadGoogleMaps(): Promise<void> {
    // Si el script ya está cargado, resuelve la promesa inmediatamente
    if (this.scriptLoaded) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleMapsApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Error al cargar la API de Google Maps'));
      };

      document.head.appendChild(script);
    });
  }
}
